import {useMemo} from 'react';
import ObservabilityConfigContext from 'src/internal/config/ObservabilityConfigContext';
import {DEFAULT_OBSERVABILITY_CONFIG} from 'src/internal/constants';
import useObservabilityConfig from 'src/public/useObservabilityConfig';

import type {ReactElement, ReactNode} from 'react';
import type {ObservabilityConfig} from 'src/types';

type Props = {
  config: ObservabilityConfig;
  children: ReactNode;
};

function mergeKeyedParams<T extends Record<string, unknown>>(
  parent?: Readonly<Record<string, T>>,
  child?: Readonly<Record<string, T>>,
): Record<string, T> | undefined {
  if (!parent && !child) {
    return undefined;
  }
  const keys = new Set([
    ...Object.keys(parent ?? {}),
    ...Object.keys(child ?? {}),
  ]);
  const result: Record<string, T> = {};
  for (const key of keys) {
    result[key] = {
      ...parent?.[key],
      ...child?.[key],
    } as T;
  }
  return result;
}

export default function ObservabilityConfigProvider({
  config,
  children,
}: Props): ReactElement {
  const parentConfig = useObservabilityConfig();
  const defaultConfig = DEFAULT_OBSERVABILITY_CONFIG;
  const mergedConfig = useMemo(
    () => ({
      ...parentConfig,
      ...config,
      errors: {
        ...parentConfig?.errors,
        ...config.errors,
        projects: {
          ...defaultConfig?.errors?.projects,
          ...parentConfig?.errors?.projects,
          ...config?.errors?.projects,
        },
      },
      analytics: {
        ...parentConfig?.analytics,
        ...config?.analytics,
        parametersAllowList: Array.from(
          new Set([
            ...(parentConfig?.analytics?.parametersAllowList ?? []),
            ...(config?.analytics?.parametersAllowList ?? []),
          ]),
        ),
        parameters: mergeKeyedParams(
          parentConfig?.analytics?.parameters,
          config?.analytics?.parameters,
        ),
      },
      metrics: {
        ...parentConfig?.metrics,
        ...config?.metrics,
        parametersAllowList: Array.from(
          new Set([
            ...(parentConfig?.metrics?.parametersAllowList ?? []),
            ...(config?.metrics?.parametersAllowList ?? []),
          ]),
        ),
        parameters: mergeKeyedParams(
          parentConfig?.metrics?.parameters,
          config?.metrics?.parameters,
        ),
      },
      performance: {
        ...parentConfig?.performance,
        ...config?.performance,
      },
    }),
    [parentConfig, defaultConfig, config],
  );

  return (
    <ObservabilityConfigContext.Provider value={mergedConfig}>
      {children}
    </ObservabilityConfigContext.Provider>
  );
}
